import { useRef, useImperativeHandle, forwardRef } from "react";
import { WPRequests } from "@/components/reserve/WPRequests";
import { ISchedule } from "@gettactic/api";
import { TooltipProvider } from "@gettactic/components/src/@v2/components/ui/tooltip";
import { UseFormReturn } from "react-hook-form";
import { SaveReservationData } from "@/lib/utils-reserve";
import {
  WorkplaceRequestParams,
  WorkplaceRequestsRef
} from "@/types/workplace-requests";
import { isWorkplaceRequestsEnabled } from "@/lib/featureFlags";
import { useIsDesignSystem2, useOrgFeatureFlag } from "@gettactic/hooks";
import { RequestTypeSelector } from "@/components/reserve/Reserve/controls/ReserveFormWPRequests";

interface ReserveEditFormWPRequestsProps {
  schedule: ISchedule;
  officeId: string;
  form: UseFormReturn<SaveReservationData>;
}

export const ReserveEditFormWPRequests = forwardRef<
  WorkplaceRequestsRef,
  ReserveEditFormWPRequestsProps
>(({ schedule, officeId, form }, ref) => {
  const wpRequestsRef = useRef<WorkplaceRequestsRef>();
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag('workplace_requests');
  const shouldShow = isWorkplaceRequestsEnabled(workplaceRequestsFeatureFlag);
  const isV2 = useIsDesignSystem2();

  // Expose the submit function to the parent component
  useImperativeHandle(
    ref,
    () => ({
      submitWorkplaceRequests: (params: WorkplaceRequestParams) => {
        if (wpRequestsRef.current?.submitWorkplaceRequests) {
          return wpRequestsRef.current.submitWorkplaceRequests(params);
        }
        return Promise.resolve({ success: true });
      },
      validateAndGetRequests: () => {
        if (wpRequestsRef.current?.validateAndGetRequests) {
          return wpRequestsRef.current.validateAndGetRequests();
        }
        return Promise.resolve(null);
      }
    }),
    []
  );

  const recurringValue = form.watch("recurring");
  const isRecurring =
    recurringValue && recurringValue !== "" && recurringValue !== "no";

  if (isRecurring || !shouldShow || !isV2) {
    return null;
  }

  return (
    <div className="pt-4" onClick={(e) => e.stopPropagation()}>
      <TooltipProvider>
        <WPRequests
          reservationId={schedule.id}
          officeId={officeId}
          onSubmitRef={wpRequestsRef}
          className="border-gray-200 border-t pt-4"
          customTypeSelector={RequestTypeSelector}
        />
      </TooltipProvider>
    </div>
  );
});

ReserveEditFormWPRequests.displayName = "ReserveEditFormWPRequests";
